import logger from './utils/logger.js';
import 'cookies-next';
import './currency-_kacFJHO.js';
import './language-BHHwgtQp.js';
import './payment-5CMab_Gs.js';
import './utils/currency.js';
import 'lodash/get';
import './utils/price.js';
import './product-_pRduMz7.js';
import 'react';
import './context/index.js';
import 'next/server';
import './index-Oip0gjfj.js';
import 'next/router';
import './helpers-qyZpLI0y.js';
import 'lodash';
import './index-QrdBfapu.js';

/**
 * KWK UniJS service for Accor affiliate retargeting
 * Provides utilities to load the KWK UniJS library and fire tracking events
 */
// Module-level promise to ensure script loads only once
let initPromise = null;
// Track fired events to prevent duplicates
const firedEvents = new Set();
/**
 * Generate a unique key for an event to prevent duplicates
 * @param zone - The tracking zone
 * @param identifier - Optional identifier (productId, transactionId, etc.)
 * @param consent - The consent status to include in the key
 * @returns Unique event key
 */
const getEventKey = (zone, identifier, consent) => {
    const baseKey = identifier ? `${zone}:${String(identifier)}` : zone;
    return consent ? `${baseKey}:${consent}` : baseKey;
};
/**
 * Check if an event has already been fired
 * @param eventKey - The event key to check
 * @returns True if event was already fired
 */
const hasEventFired = (eventKey) => firedEvents.has(eventKey);
/**
 * Mark an event as fired
 * @param eventKey - The event key to mark as fired
 */
const markEventFired = (eventKey) => {
    firedEvents.add(eventKey);
};
/**
 * Initialize KWK UniJS library by loading the script
 * @param options - Configuration for script URL
 * @returns Promise that resolves when KWKUniJS is available
 */
const initKwanko = async ({ scriptUrl }) => {
    // Return existing promise if already initializing
    if (initPromise) {
        return initPromise;
    }
    // Check if already loaded
    if (window.KWKUniJS) {
        return Promise.resolve();
    }
    initPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.src = scriptUrl;
        script.async = true;
        script.onload = () => {
            // Wait for KWKUniJS to be available
            const checkKwanko = () => {
                if (window.KWKUniJS) {
                    logger('[KWK UniJS] Library loaded successfully', { category: 'analytics', type: 'log' });
                    resolve();
                }
                else {
                    setTimeout(checkKwanko, 100);
                }
            };
            checkKwanko();
        };
        script.onerror = () => {
            logger('[KWK UniJS] Failed to load library', { category: 'analytics', type: 'error' });
            reject(new Error('Failed to load KWK UniJS library'));
        };
        document.head.appendChild(script);
    });
    return initPromise;
};
/**
 * Get consent status for KWK tracking
 * @returns '1' if consent given, '0' otherwise
 */
const getKwkConsent = () => {
    var _a, _b, _c, _d;
    // Primary: Cookiebot marketing consent (most specific for affiliate tracking)
    if (((_b = (_a = window.Cookiebot) === null || _a === void 0 ? void 0 : _a.consent) === null || _b === void 0 ? void 0 : _b.marketing) === true) {
        return '1';
    }
    // Secondary: ConsentMode advertising consent (relevant for affiliate tracking)
    if (((_d = (_c = window.ConsentMode) === null || _c === void 0 ? void 0 : _c.consent) === null || _d === void 0 ? void 0 : _d.advertising) === true) {
        return '1';
    }
    // For affiliate tracking, we require explicit marketing/advertising consent
    // General preferences consent is not sufficient for tracking purposes
    return '0';
};
/**
 * Check if consent has been properly determined (similar to existing GTM pattern)
 * @returns True if consent is definitively determined (either granted or denied)
 */
const isConsentDetermined = () => {
    var _a, _b, _c, _d, _e, _f;
    // Check if any consent system has a definitive boolean value (true or false)
    return typeof ((_b = (_a = window.Cookiebot) === null || _a === void 0 ? void 0 : _a.consent) === null || _b === void 0 ? void 0 : _b.marketing) === 'boolean' ||
        typeof ((_d = (_c = window.ConsentMode) === null || _c === void 0 ? void 0 : _c.consent) === null || _d === void 0 ? void 0 : _d.advertising) === 'boolean' ||
        typeof ((_f = (_e = window.CookieConsent) === null || _e === void 0 ? void 0 : _e.consent) === null || _f === void 0 ? void 0 : _f.preferences) === 'boolean';
};
/**
 * Fire KWK ptag with consent automatically added
 * @param payload - Tracking payload (gdpr_kwk will be added automatically)
 * @param identifier - Optional identifier for idempotency (productId, transactionId, etc.)
 * @param force - Optional flag to force the event to be fired and skip the idempotency check
 */
const ptag = async (payload, identifier, force) => {
    try {
        const scriptUrl = process.env.NEXT_PUBLIC_ACCOR_AFFILIATE_PROGRAM_UNIJS_URL;
        if (!scriptUrl) {
            logger('[KWK UniJS] UniJS URL is not configured, skipping tracking call', {
                category: 'analytics',
                type: 'log',
            });
            return;
        }
        await initKwanko({ scriptUrl });
        if (!window.KWKUniJS) {
            logger('[KWK UniJS] Library not available for ptag', {
                category: 'analytics',
                type: 'error',
            });
            return;
        }
        // Get current consent status (will be '0' if not determined yet)
        const consent = getKwkConsent();
        // Check for idempotency using consent status in the key
        const eventKey = getEventKey(payload.zone, identifier, consent);
        if (!force && hasEventFired(eventKey)) {
            logger(`[KWK UniJS] Event already fired for ${eventKey}, skipping`, {
                category: 'analytics',
                type: 'log',
            });
            return;
        }
        const fullPayload = {
            ...payload,
            gdpr_kwk: consent,
        };
        window.KWKUniJS.ptag(fullPayload);
        markEventFired(eventKey);
        logger(`[KWK UniJS] ptag fired for zone: ${payload.zone} with consent: ${consent}, payload: ${JSON.stringify(fullPayload)}`, {
            category: 'analytics',
            type: 'log',
        });
    }
    catch (error) {
        logger(`[KWK UniJS] Error firing ptag: ${error}`, { category: 'analytics', type: 'error' });
    }
};
/**
 * Track homepage view
 */
const trackHomepage = async () => {
    await ptag({ zone: 'homepage' });
};
/**
 * Track product page view
 * @param productId - Product SKU
 * @param categoryId - Optional category ID
 */
const trackProduct = async ({ productId, categoryId, }) => {
    await ptag({ zone: 'product', productId, categoryId }, productId);
};
/**
 * Track listing page view
 * @param categoryId - Optional category ID
 * @param products - Array of product SKUs
 */
const trackListing = async ({ categoryId, products, }) => {
    // Use categoryId as identifier if available, otherwise use first product ID
    const identifier = categoryId || products[0];
    await ptag({ zone: 'listing', categoryId, products }, identifier);
};
/**
 * Track basket/cart view
 * @param products - Array of products with id, price, quantity
 * @param currency - Currency code (ISO 4217)
 */
const trackBasket = async ({ products, currency, force, }) => {
    // Use first product ID as identifier for basket, or undefined if products is empty
    const identifier = products.length > 0 ? products[0].id : undefined;
    await ptag({ zone: 'basket', products, currency }, identifier, force);
};
/**
 * Track transaction/purchase
 * @param transactionId - Order ID
 * @param products - Array of products with id, price, quantity
 * @param currency - Currency code (ISO 4217)
 */
const trackTransaction = async ({ transactionId, products, currency, }) => {
    await ptag({ zone: 'transaction', transactionId, products, currency }, transactionId);
};
/**
 * Track lead inscription (registration form view)
 */
const trackLeadInscription = async () => {
    await ptag({ zone: 'lead_inscription' });
};
/**
 * Track lead confirmation (successful registration)
 * @param registrationId - User/customer ID
 */
const trackLeadConfirmation = async ({ registrationId, }) => {
    await ptag({ zone: 'lead_confirmation', id_lead: registrationId }, registrationId);
};

var KwkUni = /*#__PURE__*/Object.freeze({
    __proto__: null,
    getKwkConsent: getKwkConsent,
    initKwanko: initKwanko,
    isConsentDetermined: isConsentDetermined,
    ptag: ptag,
    trackBasket: trackBasket,
    trackHomepage: trackHomepage,
    trackLeadConfirmation: trackLeadConfirmation,
    trackLeadInscription: trackLeadInscription,
    trackListing: trackListing,
    trackProduct: trackProduct,
    trackTransaction: trackTransaction
});

export { KwkUni as K, trackProduct as a, trackListing as b, trackBasket as c, trackTransaction as d, trackLeadInscription as e, trackLeadConfirmation as f, getKwkConsent as g, initKwanko as h, isConsentDetermined as i, ptag as p, trackHomepage as t };
//# sourceMappingURL=kwkUni-2OQfB-dG.js.map
